/** What the web client asks of the Android app's native layer (src/native). Always null on the website. */
export interface NativeApp {
  /** Keeps the session token in the Android Keystore and remembers which account is signed in. */
  signedIn(token: string, userId: string): Promise<void>;
  /**
   * Forgets the token, removes this account's notifications and retires the phone's FCM token. With
   * `revokeLater`, the server could not be told, so the session is ended the next time it can be reached.
   */
  signedOut(revokeLater?: boolean): Promise<void>;
  /** The conversation on screen while the app is in front; its notifications are held back. */
  viewingChanged(channelId: string | null): void;
  /** The conversation was read: remove its notification. */
  conversationRead(channelId: string): void;
  /** Removes notifications for conversations with nothing unread any more (after connecting). */
  syncNotifications(): void;
  /** The app is in the foreground. */
  readonly foreground: boolean;
  /** This phone is registered for push notifications, which then replace in-app notifications. */
  readonly pushActive: boolean;
  /** Push controls for settings and the permission prompt; state lives in usePushState. */
  push: {
    /** Asks Android for permission if needed (or opens its settings once denied), then registers. */
    enable(): Promise<void>;
    disable(): Promise<void>;
    setPreviews(on: boolean): Promise<void>;
    dismissPrompt(): void;
    openSettings(): void;
    sendTest(): Promise<void>;
  };
  /** The update screen's buttons; state lives in useAppUpdate. */
  update: {
    /** Downloads the offered APK and opens Android's installer. */
    install(): Promise<void>;
    /** Hides the screen until a newer version (not offered for required ones). */
    later(): void;
  };
}

export let native: NativeApp | null = null;

/**
 * Starts the native layer before the client renders, so the stored session token is ready for the first
 * request. `__NATIVE__` is a build constant: on the website this returns at once and the module isn't bundled.
 */
export async function startNative(): Promise<void> {
  if (!__NATIVE__) return;
  const { start } = await import('../native');
  native = await start();
}
